"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.appManifestV2Schema = exports.appAutomaticProvisioningSchema = exports.appListingSchema = exports.appMediaAssetSchema = void 0;
exports.parseAppManifestV2 = parseAppManifestV2;
exports.validateAppManifestRuntimeUrls = validateAppManifestRuntimeUrls;
const zod_1 = require("zod");
const events_1 = require("./events");
const permissions_1 = require("./permissions");
const ui_extensions_1 = require("./ui-extensions");
const semverSchema = zod_1.z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/);
const appIdSchema = zod_1.z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/);
const externalUrlSchema = zod_1.z
    .string()
    .url()
    .refine((value) => new URL(value).protocol === "https:", {
    message: "An HTTPS URL is required.",
})
    .refine((value) => {
    const url = new URL(value);
    return !url.username && !url.password;
}, {
    message: "URL credentials are not allowed.",
});
const appMediaUrlSchema = zod_1.z
    .string()
    .url()
    .refine((value) => {
    const url = new URL(value);
    return (url.protocol === "https:" ||
        (url.protocol === "http:" && isDevelopmentHostname(url.hostname)));
}, "An HTTPS URL or local development URL is required.");
const appRuntimeUrlSchema = appMediaUrlSchema.refine((value) => {
    const url = new URL(value);
    return !url.username && !url.password;
}, "URL credentials are not allowed.");
const appIconSchema = zod_1.z.union([
    zod_1.z.object({ kind: zod_1.z.literal("TRUSTED_KEY"), key: appIdSchema }),
    zod_1.z.object({ kind: zod_1.z.literal("URL"), url: externalUrlSchema }),
]);
exports.appMediaAssetSchema = zod_1.z.object({
    kind: zod_1.z.enum(["SCREENSHOT", "VIDEO"]),
    url: appMediaUrlSchema,
    alt: zod_1.z.string().trim().min(1).max(160),
    caption: zod_1.z.string().trim().min(1).max(240).optional(),
});
exports.appListingSchema = zod_1.z.object({
    tagline: zod_1.z.string().trim().min(1).max(120),
    overview: zod_1.z.string().trim().min(1).max(800),
    highlights: zod_1.z.array(zod_1.z.string().trim().min(1).max(160)).min(1).max(6),
});
const appConfigurationFieldSchema = zod_1.z.object({
    id: appIdSchema,
    label: zod_1.z.string().trim().min(1).max(80),
    type: zod_1.z.enum(["TEXT", "URL", "BOOLEAN", "SELECT", "SECRET"]),
    required: zod_1.z.boolean().default(false),
    secret: zod_1.z.boolean().default(false),
    description: zod_1.z.string().trim().min(1).max(240).optional(),
    options: zod_1.z.array(zod_1.z.string().trim().min(1).max(80)).max(50).optional(),
});
const appDataRetentionPolicySchema = zod_1.z.object({
    coreData: zod_1.z.literal("RETAIN"),
    appOwnedData: zod_1.z.enum(["RETAIN", "DELETE", "ANONYMIZE"]),
    appOwnedDataRetentionDays: zod_1.z.number().int().min(0).max(3_650).optional(),
    auditHistory: zod_1.z.literal("RETAIN"),
});
const appUninstallPolicySchema = zod_1.z.object({
    revokeCredentials: zod_1.z.literal(true),
    stopEventDeliveries: zod_1.z.literal(true),
    retainDeliveryHistory: zod_1.z.literal(true),
    retainAuditHistory: zod_1.z.literal(true),
});
exports.appAutomaticProvisioningSchema = zod_1.z.object({
    protocolVersion: zod_1.z.literal("1.0"),
    eventDelivery: zod_1.z
        .object({
        endpointUrl: appRuntimeUrlSchema,
    })
        .optional(),
    lifecycleCallbacks: zod_1.z.object({
        installUrl: appRuntimeUrlSchema,
        updateUrl: appRuntimeUrlSchema,
        uninstallUrl: appRuntimeUrlSchema,
    }),
});
exports.appManifestV2Schema = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal("2.0"),
    id: appIdSchema,
    name: zod_1.z.string().trim().min(1).max(80),
    version: semverSchema,
    publisher: zod_1.z.object({
        id: appIdSchema,
        name: zod_1.z.string().trim().min(1).max(80),
        kind: ui_extensions_1.appPublisherKindSchema,
    }),
    description: zod_1.z.string().trim().min(1).max(500),
    category: zod_1.z.string().trim().min(1).max(80),
    icon: appIconSchema,
    listing: exports.appListingSchema.optional(),
    media: zod_1.z.array(exports.appMediaAssetSchema).max(8).default([]),
    deliveryModel: ui_extensions_1.appDeliveryModelSchema,
    uiRuntime: ui_extensions_1.appUiRuntimeSchema,
    commercialModel: ui_extensions_1.appCommercialModelSchema,
    supportedPlatforms: zod_1.z.array(ui_extensions_1.appSupportedPlatformSchema).min(1).max(3),
    permissions: zod_1.z.array(permissions_1.appPermissionSchema).default([]),
    subscriptions: zod_1.z.array(events_1.builtInIntegrationEventTypeSchema).default([]),
    automaticProvisioning: exports.appAutomaticProvisioningSchema.optional(),
    planRequirements: zod_1.z.array(zod_1.z.string().trim().min(1).max(80)).default([]),
    capabilityRequirements: zod_1.z
        .array(zod_1.z.string().trim().min(1).max(120))
        .default([]),
    configuration: zod_1.z.array(appConfigurationFieldSchema).default([]),
    supportUrl: externalUrlSchema,
    privacyUrl: externalUrlSchema,
    termsUrl: externalUrlSchema,
    dataRetention: appDataRetentionPolicySchema,
    uninstall: appUninstallPolicySchema,
    appHome: ui_extensions_1.appHomeSchema.optional(),
    navigation: zod_1.z.array(ui_extensions_1.appNavigationItemSchema).max(8).default([]),
    uiExtensions: zod_1.z.array(ui_extensions_1.appUiExtensionSchema).default([]),
    minimumHostApiVersion: semverSchema,
})
    .superRefine((manifest, context) => {
    if (manifest.uiRuntime === "NONE") {
        if (manifest.appHome ||
            manifest.navigation.length ||
            manifest.uiExtensions.length) {
            context.addIssue({
                code: "custom",
                message: "A NONE UI runtime cannot declare UI contributions.",
                path: ["uiRuntime"],
            });
        }
    }
    else if (!manifest.appHome) {
        context.addIssue({
            code: "custom",
            message: "A UI runtime requires an app home declaration.",
            path: ["appHome"],
        });
    }
    if (manifest.uiRuntime === "NATIVE" &&
        manifest.publisher.kind !== "CREWFSM") {
        context.addIssue({
            code: "custom",
            message: "Only CrewFSM-published apps can use the NATIVE runtime.",
            path: ["publisher", "kind"],
        });
    }
    if (manifest.deliveryModel === "CORE_ADDON") {
        if (manifest.publisher.kind !== "CREWFSM" ||
            manifest.uiRuntime === "IFRAME") {
            context.addIssue({
                code: "custom",
                message: "A core add-on must be CrewFSM-published and cannot use the iframe runtime.",
                path: ["deliveryModel"],
            });
        }
        if (manifest.subscriptions.length) {
            context.addIssue({
                code: "custom",
                message: "A core add-on cannot declare external integration event subscriptions.",
                path: ["subscriptions"],
            });
        }
    }
    if (manifest.automaticProvisioning) {
        if (manifest.deliveryModel === "CORE_ADDON") {
            context.addIssue({
                code: "custom",
                message: "A core add-on cannot declare an external provisioning runtime.",
                path: ["automaticProvisioning"],
            });
        }
        if (manifest.subscriptions.length > 0 &&
            !manifest.automaticProvisioning.eventDelivery) {
            context.addIssue({
                code: "custom",
                message: "An automatically provisioned event consumer requires an event delivery endpoint.",
                path: ["automaticProvisioning", "eventDelivery"],
            });
        }
        if (manifest.subscriptions.length === 0 &&
            manifest.automaticProvisioning.eventDelivery) {
            context.addIssue({
                code: "custom",
                message: "An event delivery endpoint requires at least one declared subscription.",
                path: ["automaticProvisioning", "eventDelivery"],
            });
        }
    }
    if (manifest.appHome && manifest.appHome.renderer !== manifest.uiRuntime) {
        context.addIssue({
            code: "custom",
            message: "App home renderer must match the declared UI runtime.",
            path: ["appHome", "renderer"],
        });
    }
    const manifestPermissions = new Set(manifest.permissions);
    for (const [index, item] of manifest.navigation.entries()) {
        validatePermissionSubset(item.requiredPermissions, manifestPermissions, context, ["navigation", index, "requiredPermissions"]);
    }
    for (const [index, extension] of manifest.uiExtensions.entries()) {
        validatePermissionSubset(extension.requiredPermissions, manifestPermissions, context, ["uiExtensions", index, "requiredPermissions"]);
        if (extension.renderer === "NATIVE" &&
            manifest.publisher.kind !== "CREWFSM") {
            context.addIssue({
                code: "custom",
                message: "Only CrewFSM-published apps can declare native extensions.",
                path: ["uiExtensions", index, "renderer"],
            });
        }
    }
    validateUnique(manifest.navigation.map((item) => item.id), context, ["navigation"], "navigation id");
    validateUnique(manifest.navigation.map((item) => item.path), context, ["navigation"], "navigation path");
    validateUnique([
        ...manifest.navigation.map((item) => item.path),
        ...manifest.uiExtensions
            .filter((extension) => extension.type === "NAV_ITEM")
            .map((extension) => extension.route),
    ], context, ["navigation"], "navigation path");
    validateUnique(manifest.uiExtensions.map((extension) => extension.id), context, ["uiExtensions"], "extension id");
    validateUnique(manifest.configuration.map((field) => field.id), context, ["configuration"], "configuration field id");
});
function parseAppManifestV2(input) {
    return exports.appManifestV2Schema.parse(input);
}
/**
 * Applies the environment-sensitive part of manifest URL validation.
 *
 * The Zod contract permits HTTP only for explicit development hostnames.
 * Development hosts and literal private-network addresses are rejected by
 * default. A host must additionally perform DNS resolution before making an
 * outbound request so a public-looking hostname cannot resolve privately.
 */
function validateAppManifestRuntimeUrls(manifest, policy = {}) {
    const urls = [
        manifest.appHome?.renderer === "IFRAME"
            ? manifest.appHome.entryUrl
            : undefined,
        ...manifest.uiExtensions.map((extension) => extension.renderer === "IFRAME" ? extension.entryUrl : undefined),
        ...manifest.media.map((asset) => asset.url),
        manifest.automaticProvisioning?.eventDelivery?.endpointUrl,
        manifest.automaticProvisioning?.lifecycleCallbacks.installUrl,
        manifest.automaticProvisioning?.lifecycleCallbacks.updateUrl,
        manifest.automaticProvisioning?.lifecycleCallbacks.uninstallUrl,
    ].filter((value) => Boolean(value));
    for (const value of urls) {
        const url = new URL(value);
        if (url.username || url.password) {
            throw new Error("App runtime URLs cannot contain credentials.");
        }
        if (!policy.allowDevelopmentHosts &&
            (url.protocol !== "https:" ||
                isDevelopmentHostname(url.hostname) ||
                isPrivateLiteralAddress(url.hostname))) {
            throw new Error("Local, reserved, and private-network app runtime URLs are not allowed.");
        }
    }
    return manifest;
}
function validatePermissionSubset(permissions, manifestPermissions, context, path) {
    for (const permission of permissions) {
        if (!manifestPermissions.has(permission)) {
            context.addIssue({
                code: "custom",
                message: `UI contribution requires undeclared permission ${permission}.`,
                path,
            });
        }
    }
}
function validateUnique(values, context, path, label) {
    if (new Set(values).size !== values.length) {
        context.addIssue({
            code: "custom",
            message: `Duplicate ${label} values are not allowed.`,
            path,
        });
    }
}
function isDevelopmentHostname(hostname) {
    const normalized = hostname.toLowerCase().replace(/\.$/, "");
    return (normalized === "localhost" ||
        normalized.endsWith(".localhost") ||
        normalized.endsWith(".local") ||
        normalized.endsWith(".internal") ||
        normalized.endsWith(".test"));
}
function isPrivateLiteralAddress(hostname) {
    const normalized = hostname
        .toLowerCase()
        .replace(/^\[/, "")
        .replace(/\]$/, "");
    if (normalized === "::" || normalized === "::1")
        return true;
    if (normalized.startsWith("fc") || normalized.startsWith("fd"))
        return true;
    if (normalized.startsWith("fe80:"))
        return true;
    const ipv4 = normalized.startsWith("::ffff:")
        ? normalized.slice("::ffff:".length)
        : normalized;
    const parts = ipv4.split(".").map(Number);
    if (parts.length !== 4 ||
        parts.some((part) => !Number.isInteger(part) || part < 0 || part > 255)) {
        return false;
    }
    const [first = 0, second = 0] = parts;
    return (first === 0 ||
        first === 10 ||
        first === 127 ||
        (first === 169 && second === 254) ||
        (first === 172 && second >= 16 && second <= 31) ||
        (first === 192 && second === 168) ||
        first >= 224);
}
//# sourceMappingURL=manifest-v2.js.map